import axios from 'axios';
import { adminRouteUrls } from 'config/urls';
import { AdminRouteLevel } from 'constants/adminRoutes';

/**
 * One admin-managed embedded route.
 *
 * `route_id` is the identity and is immutable — it becomes the `id` on both the
 * `screenDetailsWithRoutes` entry and the nav option, which is the key everything joins on.
 * `parent_id` / `section` are populated only for `level: 'sub'`, `description` only for
 * `level: 'parent'`.
 */
export type AdminRouteItem = {
    route_id: string;
    level: AdminRouteLevel;
    label: string;
    /** `screens[].id` of the group this sits under. Null for a top-level route. */
    parent_id: string | null;
    /** Column inside that group (`Diagnostics`, `Predictive`, …). Null for a top-level route. */
    section: string | null;
    /** Bare, no leading slash — the format `screenDetailsWithRoutes[].path` uses. */
    route_path: string;
    iframe_url: string;
    /**
     * Always an explicit boolean. An omitted value is not neutral: App.tsx reads
     * `iframe.hideGlobalFilter === true` and falls back with `??`, so `undefined` would hand the
     * decision back to `ROUTES_WITHOUT_GLOBAL_FILTER` while `false` forces the filter on.
     */
    hide_global_filter: boolean;
    /** Landing-card subtitle for a top-level route. */
    description: string | null;
    updated_at: string | null;
    updated_by: string | null;
};

export type CreateRoutePayload = Omit<AdminRouteItem, 'updated_at' | 'updated_by'>;
export type UpdateRoutePayload = { route_id: string } & Partial<Omit<CreateRoutePayload, 'route_id'>>;

type FetchRoutesResponse = { routes: AdminRouteItem[]; count: number };
type SingleRouteResponse = { route: AdminRouteItem };

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/**
 * The API returns two error shapes. Application errors (400/404/409) carry a string `detail` that
 * is safe to show; framework schema errors (422) carry a list. A 500 `detail` is never
 * user-facing copy, so it is swapped for a generic message.
 */
export function extractRouteApiError(error: any, fallback: string = GENERIC_ERROR): string {
    const status = error?.response?.status;
    if (status === 500) return fallback;

    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;

    if (Array.isArray(detail) && detail.length) {
        const [first] = detail;
        const location = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : '';
        const message = first?.msg || 'Invalid value';
        return location ? `${location}: ${message}` : message;
    }

    return fallback;
}

/** True for the duplicate-id / duplicate-path rejection, which belongs on a field, not in a toast. */
export function isRouteConflictError(error: any): boolean {
    return error?.response?.status === 409;
}

/**
 * Every route registered for this tenant, in insertion order.
 *
 * Read by `ConfigContext` for all users, not just admins — the routes have to render for
 * everyone. An empty list is a 200 with `routes: []`, never a 404.
 */
export async function fetchAdminRoutes(): Promise<AdminRouteItem[]> {
    const { data } = await axios.get<FetchRoutesResponse>(adminRouteUrls.fetchRoutes);
    return data?.routes ?? [];
}

export async function createAdminRoute(payload: CreateRoutePayload): Promise<AdminRouteItem> {
    const { data } = await axios.post<SingleRouteResponse>(adminRouteUrls.createRoute, payload);
    return data?.route;
}

/** Partial update: only the fields present are written. `route_id` addresses the row. */
export async function updateAdminRoute(payload: UpdateRoutePayload): Promise<AdminRouteItem> {
    const { data } = await axios.post<SingleRouteResponse>(adminRouteUrls.updateRoute, payload);
    return data?.route;
}

/** Hard delete — there is no soft delete and no undo, hence the confirmation in the UI. */
export async function deleteAdminRoute(routeId: string): Promise<void> {
    await axios.delete(adminRouteUrls.deleteRoute(routeId));
}
